import { useEffect, useMemo, useState } from "react";
import { createUserProtest, fetchProtestOptions } from "../../lib/rekoApi.js";
import { useAuth } from "../../auth/AuthProvider.jsx";
import CustomSelect from "../../components/CustomSelect.jsx";

const initialForm = {
    race_id: "",
    incident_timestamp: "",
    protester_season_driver_id: "",
    accused_season_driver_id: "",
    initial_rule_id: "",
    description: "",
    evidence_url: "",
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function formatDateTime(value) {
    if (!value) return "–";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function InfoTile({ label, value, tone = "info" }) {
    return (
        <div className={`rv-absence-tile rv-absence-tile--${tone}`}>
            <div className="rv-info-tile__label">{label}</div>
            <div className="rv-info-tile__value">{value}</div>
        </div>
    );
}

function ProtestDeadlineBox({ deadlines }) {
    if (!deadlines) return null;

    const isOpen = Boolean(deadlines.can_submit);
    const status = String(deadlines.status || "");

    const tone =
        isOpen
            ? "ok"
            : status === "not_open"
                ? "info"
                : "warn";

    return (
        <div
            style={{
                display: "grid",
                gap: 12,
                padding: 14,
                borderRadius: 16,
                border: isOpen
                    ? "1px solid rgba(139,217,166,.28)"
                    : "1px solid rgba(255,212,112,.30)",
                background: isOpen
                    ? "rgba(139,217,166,.07)"
                    : "rgba(255,212,112,.07)",
            }}
        >
            <div>
                <div className="rv-page-kicker">Protestfenster</div>
                <h3 style={{ margin: "4px 0 0" }}>
                    {deadlines.status_label || (isOpen ? "Protest möglich" : "Protest aktuell nicht möglich")}
                </h3>
                <p className="muted" style={{ marginTop: 6 }}>
                    {deadlines.status === "not_open"
                        ? "Die Protesteingabe ist für dieses Rennen noch nicht geöffnet."
                        : deadlines.status === "closed"
                            ? "Die Protestfrist für dieses Rennen ist bereits abgelaufen."
                            : deadlines.status === "missing_date"
                                ? "Für dieses Rennen fehlt ein Renndatum im Schedule."
                                : "Du kannst für dieses Rennen aktuell einen Protest einreichen."}
                </p>
            </div>

            <div className="rv-info-grid">
                <InfoTile
                    label="Protest möglich ab"
                    value={formatDateTime(deadlines.open_at)}
                    tone={tone}
                />

                <InfoTile
                    label="Protest möglich bis"
                    value={formatDateTime(deadlines.close_at)}
                    tone={tone}
                />

                <InfoTile
                    label="Status"
                    value={deadlines.status_label || "-"}
                    tone={tone}
                />
            </div>
        </div>
    );
}

export default function Protest() {
    const { user } = useAuth();

    const [form, setForm] = useState(initialForm);
    const [season, setSeason] = useState(null);
    const [races, setRaces] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [rules, setRules] = useState([]);
    const [currentUserSeasonDriverId, setCurrentUserSeasonDriverId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [showTsHint, setShowTsHint] = useState(false);

    const selectedRace = useMemo(() => {
        return races.find((r) => String(r.id) === String(form.race_id)) || null;
    }, [races, form.race_id]);
    const protestDeadlines = selectedRace?.protest_deadlines || null;
    const protestLocked = Boolean(protestDeadlines && !protestDeadlines.can_submit);

    const sortedDrivers = useMemo(() => {
        return [...drivers].sort((a, b) => {
            const aName = String(a?.label || "")
                .replace(/^#?\d+\s*(?:\/|-)?\s*/i, "")
                .replace(/\s*\((PRO|INT|AM)\)\s*$/i, "")
                .trim();

            const bName = String(b?.label || "")
                .replace(/^#?\d+\s*(?:\/|-)?\s*/i, "")
                .replace(/\s*\((PRO|INT|AM)\)\s*$/i, "")
                .trim();

            return aName.localeCompare(bName, "de", {
                numeric: true,
                sensitivity: "base",
            });
        });
    }, [drivers]);

    const displayName =
        user?.display_name ||
        user?.discord_username ||
        user?.email ||
        "Fahrer";

    function updateForm(key, value) {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    async function loadOptions() {
        setLoading(true);
        setErrorMsg("");

        try {
            const data = await fetchProtestOptions({ seriesKey: "gt3x" });

            setSeason(data.season || null);
            setRaces(Array.isArray(data.races) ? data.races : []);
            setDrivers(Array.isArray(data.drivers) ? data.drivers : []);
            setRules(Array.isArray(data.rules) ? data.rules : []);
            setCurrentUserSeasonDriverId(data.current_user_season_driver_id || null);

            setForm((prev) => ({
                ...prev,
                race_id: prev.race_id || String(data.current_race_id || data.races?.[0]?.id || ""),
                protester_season_driver_id:
                    prev.protester_season_driver_id ||
                    String(data.current_user_season_driver_id || ""),
            }));
        } catch (err) {
            setErrorMsg(prettyError(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();

        setSuccessMsg("");
        setErrorMsg("");

        if (!form.race_id) {
            setErrorMsg("Bitte ein Rennen auswählen.");
            return;
        }

        if (protestLocked) {
            setErrorMsg(
                protestDeadlines?.message ||
                "Die Protesteingabe ist für dieses Rennen aktuell nicht möglich."
            );
            return;
        }

        if (!form.incident_timestamp.trim()) {
            setErrorMsg("Bitte den Timestamp eintragen.");
            return;
        }

        if (!form.protester_season_driver_id) {
            setErrorMsg("Bitte deinen Fahrer auswählen.");
            return;
        }

        if (!form.description.trim()) {
            setErrorMsg("Bitte den Vorfall beschreiben.");
            return;
        }

        if (
            form.accused_season_driver_id &&
            form.accused_season_driver_id === form.protester_season_driver_id
        ) {
            setErrorMsg("Protestführer und beteiligter Fahrer dürfen nicht identisch sein.");
            return;
        }

        const payload = {
            race_id: Number(form.race_id),
            incident_timestamp: form.incident_timestamp.trim(),
            protester_season_driver_id: Number(form.protester_season_driver_id),
            accused_season_driver_id: form.accused_season_driver_id
                ? Number(form.accused_season_driver_id)
                : null,
            initial_rule_id: form.initial_rule_id
                ? Number(form.initial_rule_id)
                : null,
            description: form.description.trim(),
            evidence_url: form.evidence_url.trim() || null,
        };

        setSubmitting(true);

        try {
            const data = await createUserProtest(payload);

            setSuccessMsg(
                `Dein Protest wurde erfolgreich gespeichert. Case ${data?.case?.case_number || ""} ist jetzt bei der ReKo.`
            );

            setForm((prev) => ({
                ...initialForm,
                race_id: prev.race_id,
                protester_season_driver_id:
                    String(currentUserSeasonDriverId || prev.protester_season_driver_id || ""),
            }));
        } catch (err) {
            setErrorMsg(prettyError(err));
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="card reko-modern-page">
                <h1>Protest</h1>
                <p className="muted">Lade Protestformular…</p>
            </div>
        );
    }

    return (
        <div className="reko-modern-page">
            <section
                style={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 22,
                    padding: "24px clamp(18px, 3vw, 30px)",
                    background:
                        "radial-gradient(circle at top right, rgba(228,254,60,.14), transparent 34%), linear-gradient(135deg, rgba(35,47,45,.98), rgba(18,27,26,.98))",
                    border: "1px solid rgba(228,254,60,.18)",
                    boxShadow: "0 18px 42px rgba(0,0,0,.34)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 18,
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: "var(--accent)",
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: ".18em",
                                textTransform: "uppercase",
                                marginBottom: 8,
                            }}
                        >
                            RaceVision Protest
                        </div>

                        <h1
                            style={{
                                margin: 0,
                                fontSize: "clamp(28px, 4vw, 48px)",
                                lineHeight: 1,
                                fontStyle: "italic",
                            }}
                        >
                            Protest einreichen
                        </h1>

                        <p
                            className="muted"
                            style={{
                                marginTop: 10,
                                maxWidth: 760,
                                fontFamily: "var(--ff-Everett), sans-serif",
                            }}
                        >
                            Protestformular. Dein Protest wird direkt als ReKo-Case
                            gespeichert und ist anschließend intern für die Rennkommission sichtbar.
                        </p>
                    </div>

                    <div
                        className="pill pill--team"
                        style={{
                            alignSelf: "flex-start",
                        }}
                    >
                        Angemeldet als {displayName}
                    </div>
                </div>
            </section>

            <section
                className="card"
                style={{
                    display: "grid",
                    gap: 14,
                }}
            >
                <div>
                    <h2>Vor dem Protest</h2>
                    <p className="muted">
                        Bitte prüfe zuerst unter <strong>Incidents</strong>, ob dein Vorfall bereits
                        durch die Orga als Incident aufgenommen wurde. Falls ja, ist kein zusätzlicher Protest nötig.
                    </p>
                </div>

                {successMsg && (
                    <div className="rv-message rv-message--ok">
                        ✅ {successMsg}
                    </div>
                )}

                {errorMsg && (
                    <div className="alert alert--err">
                        {errorMsg}
                    </div>
                )}

                <ProtestDeadlineBox deadlines={protestDeadlines} />

                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: "grid",
                        gap: 14,
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 12,
                        }}
                    >
                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Rennen
                            </label>

                            <div className="rv-race-select">
                                <CustomSelect
                                    value={form.race_id}
                                    onChange={(value) => updateForm("race_id", value)}
                                    allowEmpty={false}
                                    buttonClassName="rv-select--race"
                                    options={races.map((race) => ({
                                        value: race.id,
                                        label: race.label,
                                    }))}
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 6 }}>
                                Timestamp{" "}
                                <button
                                    type="button"
                                    onClick={() => setShowTsHint((v) => !v)}
                                    style={{
                                        marginLeft: 6,
                                        borderRadius: 999,
                                        border: "1px solid rgba(228,254,60,.35)",
                                        width: 20,
                                        height: 20,
                                        fontSize: 11,
                                        lineHeight: "18px",
                                        textAlign: "center",
                                        padding: 0,
                                        background: "transparent",
                                        color: "var(--accent)",
                                        cursor: "pointer",
                                    }}
                                >
                                    i
                                </button>
                            </label>

                            <input
                                className="input"
                                value={form.incident_timestamp}
                                onChange={(e) => updateForm("incident_timestamp", e.target.value)}
                                placeholder="z. B. 18:22 oder 00:18:22"
                                disabled={protestLocked}
                                required
                            />
                        </div>

                        <div className="rv-wide-select">
                            <CustomSelect
                                value={form.protester_season_driver_id}
                                onChange={(value) => updateForm("protester_season_driver_id", value)}
                                disabled={protestLocked}
                                options={sortedDrivers.map((driver) => ({
                                    value: driver.season_driver_id,
                                    label: driver.label,
                                }))}
                            />
                        </div>

                        <div className="rv-wide-select">
                            <CustomSelect
                                value={form.accused_season_driver_id}
                                onChange={(value) => updateForm("accused_season_driver_id", value)}
                                disabled={protestLocked}
                                placeholder="Nicht sicher / mehrere Fahrer"
                                options={sortedDrivers.map((driver) => ({
                                    value: driver.season_driver_id,
                                    label: driver.label,
                                }))}
                            />
                        </div>

                    </div>

                    {showTsHint && (
                        <div
                            style={{
                                padding: 14,
                                borderRadius: 16,
                                background: "rgba(0,0,0,.35)",
                                border: "1px solid rgba(255,255,255,.08)",
                            }}
                        >
                            <p className="muted" style={{ marginBottom: 8 }}>
                                So findest du den Timestamp im iRacing-UI / Replay:
                            </p>

                            <img
                                src="/landing/TimestampExample.png"
                                alt="Beispiel: Timestamp im iRacing UI"
                                style={{
                                    width: "100%",
                                    maxWidth: 520,
                                    display: "block",
                                    borderRadius: 12,
                                }}
                            />
                        </div>
                    )}

                    <div>
                        <label style={{ display: "block", marginBottom: 6 }}>
                            Beschreibung des Vorfalls
                        </label>

                        <textarea
                            className="input"
                            rows={7}
                            style={{
                                width: "100%",
                                resize: "vertical",
                            }}
                            value={form.description}
                            onChange={(e) => updateForm("description", e.target.value)}
                            disabled={protestLocked}
                            placeholder="Bitte beschreibe den Ablauf aus deiner Sicht: Situation, Aktion, Folgen, warum du eine Sichtung wünschst …"
                            required
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <div className="muted">
                            {season?.display_name ? (
                                <>
                                    Aktive Saison: <strong>{season.display_name}</strong>
                                </>
                            ) : null}

                            {selectedRace?.label ? (
                                <>
                                    {" "}
                                    · Rennen: <strong>{selectedRace.label}</strong>
                                </>
                            ) : null}
                        </div>

                        <button
                            className="btn btn--accent"
                            type="submit"
                            disabled={submitting || protestLocked}
                            style={{
                                minWidth: 200,
                            }}
                        >
                            {submitting
                                ? "Sende Protest…"
                                : protestLocked
                                    ? "Protest aktuell nicht möglich"
                                    : "Protest absenden"}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}
